import { useEffect, useRef, type AriaAttributes } from 'react';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';
import { CalendarIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { INPUT_BASE, INPUT_PADDING, INPUT_STATE } from './input/inputStyles';

type PropsType = {
    id: string;
    /** Controlled value. `undefined` leaves the picker uncontrolled; `null` means controlled and empty. */
    value?: Date | null;
    mode?: 'single' | 'multiple' | 'range' | 'time';
    onChange: (date: Date | null) => void;
    className?: string;
    defaultDate?: flatpickr.Options.DateOption;
    placeholder?: string;
    disabled?: boolean;
    success?: boolean;
    error?: boolean;
    hint?: string;
    minDate?: flatpickr.Options.DateOption;
    maxDate?: flatpickr.Options.DateOption;
    /** Additive, optional. Names the input when the visible label repeats across rows. */
    ariaLabel?: string;
    /** #807 -- additive, optional. Overrides the value derived from `error`. */
    'aria-invalid'?: AriaAttributes['aria-invalid'];
    /** #807 -- additive, optional. Merged with the rendered `hint`'s own id. */
    'aria-describedby'?: string;
};

export function DatePicker({
    id,
    value,
    mode,
    onChange,
    className = '',
    defaultDate,
    placeholder,
    disabled = false,
    success = false,
    error = false,
    hint,
    minDate,
    maxDate,
    ariaLabel,
    'aria-invalid': ariaInvalid,
    'aria-describedby': ariaDescribedBy,
}: PropsType) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const pickerRef = useRef<flatpickr.Instance | null>(null);
    const hintId = `${id}-hint`;
    const describedBy =
        [ariaDescribedBy, hint ? hintId : undefined].filter(Boolean).join(' ') || undefined;

    // Held in a ref, never a mount dependency: callers pass an inline handler, and
    // treating its identity as a reinit key tore down flatpickr on every render.
    const onChangeRef = useRef(onChange);
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        if (!inputRef.current) {
            return;
        }

        const flatPickr = flatpickr(inputRef.current, {
            mode: mode || 'single',
            static: true,
            monthSelectorType: 'static',
            dateFormat: 'd/m/Y',
            defaultDate: value === undefined ? defaultDate : (value ?? undefined),
            minDate,
            maxDate,
            onChange: (selectedDates: Date[]) => {
                if (selectedDates && selectedDates.length > 0) {
                    onChangeRef.current(selectedDates[0]);
                } else {
                    onChangeRef.current(null);
                }
            },
        });
        pickerRef.current = flatPickr;

        return () => {
            flatPickr.destroy();
            pickerRef.current = null;
        };
        // value deliberately excluded: the sync effect below owns post-mount updates.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, defaultDate, minDate, maxDate]);

    useEffect(() => {
        if (value === undefined || !pickerRef.current) {
            return;
        }

        if (value === null) {
            pickerRef.current.clear(false);
            return;
        }

        // Avoids re-entering onChange (see the handler above) and skips a
        // no-op write when the value has not actually changed.
        if (pickerRef.current.selectedDates[0]?.getTime() === value.getTime()) {
            return;
        }
        pickerRef.current.setDate(value, false);
    }, [value]);

    const stateClasses = disabled
        ? INPUT_STATE.disabled
        : error
          ? INPUT_STATE.error
          : success
            ? INPUT_STATE.success
            : INPUT_STATE.default;
    const inputClasses = twMerge(INPUT_BASE, INPUT_PADDING, className, stateClasses);

    return (
        <>
            <div className="relative">
                <input
                    id={id}
                    ref={inputRef}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={inputClasses}
                    aria-label={ariaLabel}
                    aria-invalid={ariaInvalid ?? (error || undefined)}
                    aria-describedby={describedBy}
                />

                <span
                    className={`absolute -translate-y-1/2 pointer-events-none right-3 top-1/2 ${
                        disabled
                            ? 'text-mp-muted opacity-40'
                            : error
                              ? 'text-mp-error'
                              : success
                                ? 'text-mp-success'
                                : 'text-mp-muted'
                    }`}
                >
                    <CalendarIcon className="size-5" />
                </span>
            </div>
            {hint && (
                <p
                    id={hintId}
                    className={`mt-1.5 text-xs ${
                        error
                            ? 'text-mp-error'
                            : success
                              ? 'text-mp-success'
                              : 'text-mp-muted'
                    }`}
                >
                    {hint}
                </p>
            )}
        </>
    );
}
